import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'

import type { PlatformUserSummary } from '@/bindings/PlatformUserSummary'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { platformUserListQuery, platformUserName } from '@/features/platform-users/api'
import { useAuth } from '@/lib/auth-store'
import { Pager } from '@/components/pager'
import { useT } from '@/lib/i18n/translate'

export function PlatformUserListPage() {
  const t = useT()
  const { page } = useSearch({ from: '/platform-auth/users' })
  const list = useQuery(platformUserListQuery(page))

  const total = list.data?.total ?? 0

  return (
    <Page width="lg" title={t('users.title')} className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Link to="/users/new" className="bg-accent text-accent-fg hover:bg-accent-hover flex min-h-tap items-center rounded-lg px-4 font-medium">
          {t('users.create')}
        </Link>
      </div>

      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('users.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((user) => (
              <li key={user.id}>
                <UserRow user={user} />
              </li>
            ))}
          </ul>
        ))}

      <Pager label={t('users.pager')} page={page} total={total} link={(page) => ({ to: '/users', search: { page } })} />
    </Page>
  )
}


function UserRow({ user }: { user: PlatformUserSummary }) {
  const t = useT()
  const auth = useAuth('platform')
  const self = auth.status === 'authenticated' && auth.user.id === user.id

  return (
    <Link
      to="/users/$userId"
      params={{ userId: user.id }}
      className="bg-surface border-border hover:bg-accent-soft flex items-center gap-3 rounded-xl border p-4"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate font-medium">{platformUserName(user)}</span>
          {user.is_admin && <Badge tone="success">{t('users.admin')}</Badge>}
          {self && <Badge tone="muted">{t('users.you')}</Badge>}
        </span>
        <span className="text-muted truncate text-sm">{user.email}</span>
      </span>
    </Link>
  )
}
